import { formatLiczby } from '@/lib/i18nNumber';
import type { TFunction } from 'i18next';
import type { SourcePostFilters } from '@/hooks/useSourcePosts';

/**
 * FILTRY MIĘKKIE (SRCH-01 etap 1b; ustalenia Piotra i Maćka 28.09 —
 * docs/handoff/2026-09-28-ustalenia-filtry-piotr-maciek.md).
 *
 * Zasada, ta sama co przy zwierzętach: oferta, która SPEŁNIA warunek, jest wyżej; o której NIE
 * WIADOMO — niżej, z adnotacją „ogłoszenie nie mówi nic o …"; która WPROST NIE SPEŁNIA — wypada.
 *
 * Ten plik trzyma wszystkie trzy strony tej zasady w jednym miejscu, żeby się nie rozjechały:
 *  - `WARUNKI` — co znaczy „spełnia" dla każdej wartości filtra (wartości kolumn z bazy);
 *  - `predykatyWykluczenia` — łańcuch PostgREST „jawne nie wypada, NULL zostaje", po KOLUMNACH.
 *    Jeden wariant: z aktywnym filtrem miękkim SERP czyta ZAWSZE przez `flat.offers_ranked`
 *    (1b cz. 2), także w narysowanym obszarze (`p_geojson`), a ta funkcja zwraca prawdziwe
 *    kolumny. Wariant przez `extras` zniknął — `has_elevator` w `extras` rozjeżdża się z kolumną
 *    w 1 632 ofertach (pomiar 28.09), więc byłby gorszy, nie tylko zbędny;
 *  - `stanWarunku` — ten sam werdykt policzony dla karty, do adnotacji.
 * Kolejność „spełnia wyżej, nie wiadomo niżej" robi `flat.offers_ranked` (`preferencjeRankingu`,
 * sortowanie po `dopasowanie_klucz`).
 *
 * Wartości w bazie (zmierzone 28.09 na aktywnych): furnishing furnished 4 815 / partially 333 /
 * unfurnished 83 / NULL 2 145; kitchen_type separate / combined; floor 0 = parter (486), ujemne
 * (suterena) 2; bathrooms 1 → 4 032, 2+ → 342. Balkon/taras: `amenities_norm` (kolumna) i
 * `extras.amenities` (karta).
 */

// Wartości filtrów miękkich mieszkają od F3 w `src/lib/serpRead/types.ts` (wspólne z funkcją
// `send-filter-alerts`, plan §1.68); stąd eksport dalej dla dotychczasowych importów.
export type { Umeblowanie, Pietro, Kuchnia, Lazienki, Internet, Udogodnienie, TypBudynku, WiekBudynku } from '@/lib/serpRead/types';
export { KLUCZ_SWIATLOWODU, TYPY_BUDYNKU, UDOGODNIENIA, WIEKI_BUDYNKU } from '@/lib/serpRead/types';
import { KLUCZ_SWIATLOWODU, UDOGODNIENIA, type Udogodnienie } from '@/lib/serpRead/types';
import { wartosciTypuBudynku } from '@/lib/serpRead/predykatyMiekkie';

/** Czy lista udogodnień oferty zawiera dane udogodnienie (parking = parking albo garaż). */
function maUdogodnienie(lista: unknown[], u: Udogodnienie): boolean {
  return u === 'parking' ? lista.includes('parking') || lista.includes('garage') : lista.includes(u);
}

export type StanWarunku = 'spelnia' | 'nie_wiadomo' | 'nie_spelnia';

/** Pola, o których karta mówi „ogłoszenie nie mówi nic o …" — w kolejności priorytetu. */
export type PoleMiekkie =
  | 'zwierzeta' | 'typ_budynku' | 'wiek_budynku' | 'umeblowanie' | 'pietro' | 'kuchnia' | 'lazienki' | 'winda' | 'kaucja' | 'balkon' | 'internet'
  | `udogodnienie.${Udogodnienie}`;

type Extras = Record<string, unknown> | null | undefined;

const liczba = (v: unknown): number | null => {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && /^-?\d+$/.test(v)) return Number(v);
  return null;
};

/** Werdykt jednego filtra dla jednej oferty, z `extras` (te same klucze co kolumny). */
export function stanWarunku(pole: PoleMiekkie, filters: SourcePostFilters, extras: Extras): StanWarunku | null {
  const e = extras ?? {};
  if (pole.startsWith('udogodnienie.')) {
    const u = pole.slice('udogodnienie.'.length) as Udogodnienie;
    if (!filters.udogodnienia?.includes(u)) return null;
    // Dwa stany, jak balkon: wzmianka = spełnia, milczenie = nie wiadomo, nigdy „nie".
    const a = Array.isArray(e.amenities) ? (e.amenities as unknown[]) : [];
    return maUdogodnienie(a, u) ? 'spelnia' : 'nie_wiadomo';
  }
  switch (pole) {
    case 'zwierzeta': {
      if (!filters.zeZwierzeciem) return null;
      const v = e.pets_allowed;
      return v === true ? 'spelnia' : v === false ? 'nie_spelnia' : 'nie_wiadomo';
    }
    case 'typ_budynku': {
      if (!filters.typBudynku) return null;
      const v = typeof e.building_type === 'string' && e.building_type !== '' ? e.building_type : null;
      if (v == null) return 'nie_wiadomo';
      return wartosciTypuBudynku(filters.typBudynku).includes(v) ? 'spelnia' : 'nie_spelnia';
    }
    case 'wiek_budynku': {
      if (!filters.wiekBudynku) return null;
      const v = typeof e.building_age === 'string' && e.building_age !== '' ? e.building_age : null;
      if (v == null) return 'nie_wiadomo';
      return v === filters.wiekBudynku ? 'spelnia' : 'nie_spelnia';
    }
    case 'umeblowanie': {
      if (!filters.umeblowanie) return null;
      const v = typeof e.furnishing === 'string' ? e.furnishing : null;
      if (v == null) return 'nie_wiadomo';
      // „Umeblowane" obejmuje częściowo umeblowane (ustalenie 28.09); „Nieumeblowane" — nie.
      const ok = filters.umeblowanie === 'umeblowane' ? v === 'furnished' || v === 'partially' : v === 'unfurnished';
      return ok ? 'spelnia' : 'nie_spelnia';
    }
    case 'pietro': {
      if (!filters.pietro) return null;
      const v = liczba(e.floor);
      if (v == null) return 'nie_wiadomo';
      return v <= 0 ? 'spelnia' : 'nie_spelnia';
    }
    case 'kuchnia': {
      if (!filters.kuchnia) return null;
      const v = typeof e.kitchen_type === 'string' ? e.kitchen_type : null;
      if (v == null) return 'nie_wiadomo';
      return v === (filters.kuchnia === 'oddzielna' ? 'separate' : 'combined') ? 'spelnia' : 'nie_spelnia';
    }
    case 'lazienki': {
      if (!filters.lazienki) return null;
      const v = liczba(e.bathrooms);
      if (v == null) return 'nie_wiadomo';
      return (filters.lazienki === '1' ? v === 1 : v >= 2) ? 'spelnia' : 'nie_spelnia';
    }
    case 'winda': {
      if (!filters.winda) return null;
      // Kolumna `has_elevator` (karta dostaje ją obok `extras` na ścieżce rankingu) — ta sama,
      // którą liczy baza: `has_elevator is not false`.
      const v = e.has_elevator;
      return v === true ? 'spelnia' : v === false ? 'nie_spelnia' : 'nie_wiadomo';
    }
    case 'kaucja': {
      if (filters.kaucjaMax == null) return null;
      const v = liczba(e.deposit_pln);
      if (v == null) return 'nie_wiadomo';
      return v <= filters.kaucjaMax ? 'spelnia' : 'nie_spelnia';
    }
    case 'balkon': {
      if (!filters.balkon) return null;
      // DWA STANY: jawne „bez balkonu" prawie nie występuje (4 wiarygodne na 6 398 — pomiar
      // agenta danych w offers_ranked), więc milczenie ogłoszenia to „nie wiadomo", nie „nie".
      const a = Array.isArray(e.amenities) ? (e.amenities as unknown[]) : [];
      return a.includes('balcony') || a.includes('terrace') ? 'spelnia' : 'nie_wiadomo';
    }
    case 'internet': {
      if (!filters.internet) return null;
      // DWA STANY, jak balkon: „bez światłowodu" ogłoszenia nie piszą, więc milczenie = nie wiadomo.
      const a = Array.isArray(e.amenities) ? (e.amenities as unknown[]) : [];
      return a.includes(KLUCZ_SWIATLOWODU) ? 'spelnia' : 'nie_wiadomo';
    }
  }
}

export const POLA_MIEKKIE: readonly PoleMiekkie[] = [
  'zwierzeta', 'typ_budynku', 'wiek_budynku', 'umeblowanie', 'pietro', 'kuchnia', 'lazienki', 'winda', 'kaucja', 'balkon', 'internet',
  ...UDOGODNIENIA.map((u) => `udogodnienie.${u}` as const),
];

/** Pola aktywnych filtrów, o których ogłoszenie milczy — do adnotacji na karcie. */
export function niewiadomeOferty(filters: SourcePostFilters, extras: Extras): PoleMiekkie[] {
  return POLA_MIEKKIE.filter((p) => stanWarunku(p, filters, extras) === 'nie_wiadomo');
}

// Strona ZAPYTANIA (czy są filtry miękkie, predykaty wykluczenia, preferencje rankingu) mieszka od F3
// w `src/lib/serpRead/predykatyMiekkie.ts` — module wspólnym z funkcją `send-filter-alerts`
// (plan §1.68). Eksport dalej, żeby dotychczasowe importy działały bez zmian.
export { maMiekkieFiltry, predykatyWykluczenia, preferencjeRankingu } from '@/lib/serpRead/predykatyMiekkie';
import { maMiekkieFiltry } from '@/lib/serpRead/predykatyMiekkie';

const TAG_JEZYKA: Record<string, string> = { pl: 'pl', en: 'en', ua: 'uk', by: 'be' };

/**
 * Zdanie adnotacji: „Ogłoszenie nie mówi nic o umeblowaniu i piętrze — zapytaj właściciela".
 * Jedno zdanie na kartę, nie po jednym na pole — przy trzech filtrach trzy linie zjadałyby kartę.
 * Spójnik „i / and / і" z `Intl.ListFormat` w języku oglądającego (`ua`/`by` to nasze kody).
 */
export function zdanieNiewiadomych(pola: PoleMiekkie[], t: TFunction, jezyk: string): string | null {
  if (pola.length === 0) return null;
  const nazwy = pola.map((p) => t(`search.unknown_field.${p}`));
  // Z udogodnieniami pól może być kilkanaście — zdanie mieści się w dwóch linijkach karty tylko
  // przy krótkiej liście. Powyżej MAKS_W_ZDANIU: pierwsze trzy (kolejność priorytetu) + „N innych".
  const krotkie =
    nazwy.length > MAKS_W_ZDANIU
      ? [...nazwy.slice(0, MAKS_W_ZDANIU - 1), t('search.unknown_more', { count: nazwy.length - (MAKS_W_ZDANIU - 1) })]
      : nazwy;
  return t('search.unknown_card', { lista: listaSpojnikiem(krotkie, jezyk) });
}

const MAKS_W_ZDANIU = 4;

/** „a, b i c" w języku oglądającego — `Intl.ListFormat` (`ua`/`by` to nasze kody). */
export function listaSpojnikiem(nazwy: string[], jezyk: string): string {
  // `Intl.ListFormat` jest w Safari od 14.1, ale nie w bibliotece typów tej konfiguracji TS.
  const ListFormat = (Intl as unknown as {
    ListFormat: new (tag: string, o: { style: string; type: string }) => { format: (x: string[]) => string };
  }).ListFormat;
  return new ListFormat(TAG_JEZYKA[jezyk.slice(0, 2)] ?? 'en', { style: 'long', type: 'conjunction' }).format(nazwy);
}

// ─── KRESKA NA LIŚCIE (SRCH-24 v2, Maciek; uniwersalnie dla KAŻDEGO filtra miękkiego) ─────────

/** Pola aktywnych filtrów miękkich, w kolejności priorytetu (jak klucz dopasowania). */
export function aktywnePolaMiekkie(filters: SourcePostFilters): PoleMiekkie[] {
  return POLA_MIEKKIE.filter((p) => stanWarunku(p, filters, {}) !== null);
}

/**
 * Dane oferty do oceny filtrów miękkich — TE SAME, które ocenia ranking: winda i kaucja z
 * KOLUMN (`extras.has_elevator` rozjeżdża się z kolumną w 1 632 ofertach), udogodnienia i balkon
 * z `amenities_norm` (606 ofert ma pozycje, których `extras.amenities` nie ma — pomiar 28.09).
 * Wspólne dla karty (zdanie „Ogłoszenie nie mówi nic o …”) i kreski na liście, żeby nie
 * rozjechały się w ocenie tej samej oferty.
 */
export function daneMiekkieOferty(post: {
  extracted_extras?: unknown;
  has_elevator?: boolean | null;
  deposit_pln?: number | null;
  amenities_norm?: string[] | null;
}): Record<string, unknown> {
  return {
    ...((post.extracted_extras as Record<string, unknown> | null) ?? {}),
    ...('has_elevator' in post ? { has_elevator: post.has_elevator } : {}),
    ...('deposit_pln' in post ? { deposit_pln: post.deposit_pln } : {}),
    ...('amenities_norm' in post ? { amenities: post.amenities_norm ?? [] } : {}),
  };
}

/**
 * Indeks pierwszej oferty, która NIE potwierdza wszystkich wybranych kryteriów miękkich — przed
 * nią stoi kreska. `-1` = kreski nie ma (brak filtrów miękkich albo wszystko potwierdzone).
 * Działa, bo lista na ścieżce rankingu jest posortowana po kluczu dopasowania PRZED klasą.
 */
export function pozycjaKreski(
  posts: readonly (Parameters<typeof daneMiekkieOferty>[0] & ZeZdjeciami)[],
  filters: SourcePostFilters,
): number {
  if (!maMiekkieFiltry(filters)) return -1;
  // Tylko w sekcji ZE ZDJĘCIAMI (Piotr 29.09): oferty bez zdjęć mają własną, drugą kreskę
  // i stoją za nią wszystkie, niezależnie od dopasowania — kreska miękka nie może stanąć
  // przed pierwszą z nich tylko dlatego, że ta nie potwierdza kryteriów.
  return posts.findIndex((p) => maZdjecia(p) && niewiadomeOferty(filters, daneMiekkieOferty(p)).length > 0);
}

/** Karta ma choć jedno zdjęcie — ta sama definicja co `flat.offers.ma_zdjecia`. */
export interface ZeZdjeciami {
  photos?: readonly string[] | null;
}
export function maZdjecia(p: ZeZdjeciami): boolean {
  return (p.photos?.length ?? 0) > 0;
}

/**
 * DRUGA KRESKA (Piotr 29.09): przed pierwszą ofertą bez zdjęć. Za nią stoją WSZYSTKIE oferty
 * bez zdjęć — serwer sortuje po `ma_zdjecia` jako pierwszym kluczu, więc po tej pozycji nie ma
 * już karty ze zdjęciem (niezmiennik, test w `serpQuery.test`). -1 = nie ma takiej oferty.
 */
export function pozycjaKreskiBezZdjec(posts: readonly ZeZdjeciami[]): number {
  return posts.findIndex((p) => !maZdjecia(p));
}

/** Tyle pól wymienia nagłówek z nazwy; resztę mówi „i innych”. */
const POL_W_NAGLOWKU = 3;

/**
 * Nagłówek kreski, budowany z aktywnych filtrów (każde pole ma w i18n frazę w dopełniaczu):
 * „Pozostałe oferty — ogłoszenia nie podają: piętra, informacji o windzie i balkonu. Zapytaj
 * właściciela.” Gdy nad kreską nie ma nic: „Żadne ogłoszenie nie podaje wprost: … — oto oferty,
 * o które warto zapytać.” Bez liczb (zasada Piotra z 28.09).
 */
export function naglowekKreski(
  filters: SourcePostFilters,
  nicNadKreska: boolean,
  t: TFunction,
  jezyk: string,
  /**
   * Ile ofert ze zdjęciem stoi pod kreską (Piotr 29.09: „Pozostałe 2 399 ofert — …”). `null`,
   * dopóki licznik nie przyjdzie — wtedy nagłówek bez liczby, żeby kreska nie czekała.
   */
  liczba: number | null = null,
): string | null {
  const pola = aktywnePolaMiekkie(filters);
  if (pola.length === 0) return null;
  const nazwy = pola.slice(0, POL_W_NAGLOWKU).map((p) => t(`search.kreska.pole.${p}`));
  if (pola.length > POL_W_NAGLOWKU) nazwy.push(t('search.kreska.inne'));
  const lista = listaSpojnikiem(nazwy, jezyk);
  if (nicNadKreska) return t('search.kreska.zadne', { lista });
  if (liczba != null) return t('search.kreska.pozostale_n', { lista, count: liczba, liczba: formatLiczby(liczba, jezyk) });
  return t('search.kreska.pozostale', { lista });
}
